import { Theme, Tooltip, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import { CampaignStatus } from 'models/enums';
import { Campaign } from 'domains/campaigns/types';

const CampaignStatusToSx: Record<string, SxProps<Theme>> = {
    [CampaignStatus.DRAFT]: {
        bgcolor: 'grey.300',
        color: 'primary.main',
    },
    [CampaignStatus.SCHEDULED]: {
        bgcolor: 'white',
        color: 'primary.main',
    },
    [CampaignStatus.SENDING]: {
        bgcolor: 'warning.main',
        color: 'primary.main',
    },
    [CampaignStatus.COMPLETED]: {
        bgcolor: 'action.active',
        color: 'primary.contrastText',
    },
    [CampaignStatus.FAILED]: {
        bgcolor: 'error.main',
        color: 'primary.contrastText',
    },
    [CampaignStatus.CANCELED]: {
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
    },
    [CampaignStatus.STOPPED]: {
        bgcolor: 'other.divider',
        color: 'primary.main',
    },
    [CampaignStatus.FINISHING]: {
        bgcolor: 'other.radish',
        color: 'primary.main',
    },
    [CampaignStatus.PAUSED]: {
        bgcolor: 'other.radish',
        color: 'primary.main',
    },
    [CampaignStatus.RESUMED]: {
        bgcolor: 'other.radish',
        color: 'primary.main',
    },
    [CampaignStatus.DELETED]: {
        bgcolor: 'grey.300',
        color: 'primary.main',
    },
    // A/B Test specific statuses
    [CampaignStatus.SENDING_AB_TEST]: {
        bgcolor: 'warning.main',
        color: 'primary.main',
    },
    [CampaignStatus.PENDING_WINNER_SELECTION]: {
        bgcolor: 'other.radish',
        color: 'primary.main',
    },
    [CampaignStatus.WINNER_DECLARED]: {
        bgcolor: 'other.radish',
        color: 'primary.main',
    },
    [CampaignStatus.WINNER_SCHEDULED]: {
        bgcolor: 'white',
        color: 'primary.main',
    },
    [CampaignStatus.SENDING_WINNER]: {
        bgcolor: 'warning.main',
        color: 'primary.main',
    },
    [CampaignStatus.WINNER_SENT]: {
        bgcolor: 'action.active',
        color: 'primary.contrastText',
    },
    [CampaignStatus.WINNER_FAILED]: {
        bgcolor: 'error.main',
        color: 'primary.contrastText',
    },
    [CampaignStatus.WINNER_STOPPED]: {
        bgcolor: 'other.divider',
        color: 'primary.main',
    },
};

export const getCampaignStatusSx = (campaignOrStatus: Campaign | CampaignStatus): SxProps<Theme> => {
    // Handle both campaign object and direct status string
    const isCampaign = typeof campaignOrStatus === 'object';
    const status = isCampaign ? campaignOrStatus.status : campaignOrStatus;
    const campaign = isCampaign ? campaignOrStatus : null;

    // WINNER_DECLARED with 100% test should use COMPLETED styling
    if (status === CampaignStatus.WINNER_DECLARED && campaign?.abTestConfig?.testPercentage === 100) {
        return CampaignStatusToSx[CampaignStatus.COMPLETED];
    }

    return CampaignStatusToSx[status] || {};
};

/**
 * Get the display text for a campaign status.
 * For WINNER_DECLARED status, the display text depends on the test percentage:
 * - 100% test: shows "completed" (no holdout group)
 * - <100% test: shows "winner declared" (has holdout group pending)
 */
export const getCampaignStatusDisplayText = (campaignOrStatus: Campaign | CampaignStatus): string => {
    // Handle both campaign object and direct status string
    const isCampaign = typeof campaignOrStatus === 'object';
    const status = isCampaign ? campaignOrStatus.status : campaignOrStatus;
    const campaign = isCampaign ? campaignOrStatus : null;

    // Handle WINNER_DECLARED with conditional logic based on test percentage
    if (status === CampaignStatus.WINNER_DECLARED) {
        const testPercentage = campaign?.abTestConfig?.testPercentage;
        return testPercentage === 100 ? 'completed' : 'winner declared';
    }

    // Default mappings for all other statuses
    const statusMap: Record<string, string> = {
        [CampaignStatus.DRAFT]: 'draft',
        [CampaignStatus.SCHEDULED]: 'scheduled',
        [CampaignStatus.SENDING]: 'sending',
        [CampaignStatus.COMPLETED]: 'completed',
        [CampaignStatus.FAILED]: 'failed',
        [CampaignStatus.CANCELED]: 'canceled',
        [CampaignStatus.STOPPED]: 'stopped',
        [CampaignStatus.FINISHING]: 'finishing',
        [CampaignStatus.PAUSED]: 'paused',
        [CampaignStatus.RESUMED]: 'resumed',
        [CampaignStatus.DELETED]: 'deleted',
        // A/B Test specific statuses
        [CampaignStatus.SENDING_AB_TEST]: 'sending a/b test',
        [CampaignStatus.PENDING_WINNER_SELECTION]: 'pending winner selection',
        [CampaignStatus.WINNER_SCHEDULED]: 'winner scheduled',
        [CampaignStatus.SENDING_WINNER]: 'sending winner',
        [CampaignStatus.WINNER_SENT]: 'completed',
        [CampaignStatus.WINNER_FAILED]: 'failed',
        [CampaignStatus.WINNER_STOPPED]: 'stopped',
    };

    return statusMap[status] || status;
};

export const getTooltipTitle = (campaignOrStatus: Campaign | CampaignStatus) => {
    // Handle both campaign object and direct status string
    const status = typeof campaignOrStatus === 'object' ? campaignOrStatus.status : campaignOrStatus;

    const CampaignStatusToToolTipTitle: Record<string, string> = {
        [CampaignStatus.CANCELED]: 'Curation will not publish.',
        [CampaignStatus.COMPLETED]: ``,
        [CampaignStatus.DRAFT]: ``,
        [CampaignStatus.FAILED]: `Curation failed - contact support for details.`,
        [CampaignStatus.FINISHING]:
            'Qualified contacts within the flow will finish and no additional contacts will enter.',
        [CampaignStatus.SCHEDULED]: ``,
        [CampaignStatus.SENDING]: ``,
        [CampaignStatus.STOPPED]:
            'Qualified contacts within the flow will not move forward and the Curation will stop.',
        [CampaignStatus.PAUSED]: 'Contacts will stay at their current step in the flow until the Curation is resumed.',
        [CampaignStatus.RESUMED]: ``,
        [CampaignStatus.DELETED]: ``,
        // A/B Test specific statuses
        [CampaignStatus.SENDING_AB_TEST]: ``,
        [CampaignStatus.PENDING_WINNER_SELECTION]: ``,
        [CampaignStatus.WINNER_DECLARED]: ``,
        [CampaignStatus.WINNER_SCHEDULED]: ``,
        [CampaignStatus.SENDING_WINNER]: ``,
        [CampaignStatus.WINNER_SENT]: ``,
        [CampaignStatus.WINNER_FAILED]: ``,
        [CampaignStatus.WINNER_STOPPED]: ``,
    };

    return CampaignStatusToToolTipTitle[status] || '';
};

export type Props = {
    campaign?: Campaign;
    status?: CampaignStatus;
};

const CampaignStatusText = ({ campaign, status }: Props) => {
    // Determine the effective status: use direct status if provided, otherwise get from campaign
    const effectiveStatus = status || campaign?.status;

    if (!effectiveStatus) {
        return null;
    }

    // Use campaign object if available for full context (e.g., abTestConfig), otherwise use status directly
    const campaignOrStatus = campaign || effectiveStatus;

    const title = getTooltipTitle(campaignOrStatus);
    const displayText = getCampaignStatusDisplayText(campaignOrStatus);
    const statusSx = getCampaignStatusSx(campaignOrStatus);

    return (
        <Tooltip aria-label={title} data-testid="campaign-status-tooltip" title={title}>
            <Typography
                className="status-font"
                component="span"
                data-testid="campaign-status-text"
                role="status"
                sx={{
                    ...statusSx,
                    borderRadius: '0.25rem',
                    padding: '0.125rem 0.75rem 0.25rem 0.75rem',
                    whiteSpace: 'nowrap',
                }}
            >
                {displayText}
            </Typography>
        </Tooltip>
    );
};

export default CampaignStatusText;
